import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { portalCommands } from 'src/app/utils/portal-path';
import { TranslateService } from '@ngx-translate/core';
import { StudentsService } from 'src/app/services/students.service';
import { ClassesService } from 'src/app/services/classes.service';
import { LanguageService } from 'src/app/services/language.service';
import { SchoolSettingsService, DEFAULT_STUDENT_CARD_COLORS, StudentCardColors } from 'src/app/services/school-settings.service';
import { StudentCardsPdfService } from 'src/app/services/student-cards-pdf.service';
import { StudentsListPdfService } from 'src/app/services/students-list-pdf.service';
import { LocationsService } from 'src/app/services/locations.service';
import { SearchableSelectOption } from 'src/app/shared/searchable-select/searchable-select.component';

export interface StudentListItem {
  id: string;
  firstNameAr: string;
  lastNameAr: string;
  firstNameEn: string;
  lastNameEn: string;
  studentCode?: string;
  qrCode?: string;
  nationalId?: string;
  dateOfBirth?: string;
  enrollmentDate?: string;
  status?: string;
  sex?: string;
  photo?: string;
  phones?: Array<{ value?: string; number?: string; isPrimary?: boolean; label?: string }>;
  emails?: Array<{ value?: string; address?: string; isPrimary?: boolean; label?: string }>;
  address?: {
    emirate?: string;
    area?: string;
    line1?: string;
    line2?: string;
    building?: string;
    flat?: string;
    landmark?: string;
  } | null;
  nationality?: { id: string; name?: string } | null;
  religion?: { id: string; name?: string } | null;
  academicYear?: { id: string; name?: string; code?: string } | null;
  class?: { id: string; name: string; major?: { id: string; name: string } | null } | null;
  bus?: {
    id: string;
    name?: string;
    number?: string;
    plateNumber?: string;
    driverName?: string;
    driverPhone?: string;
  } | null;
  parents?: Array<{
    parent?: {
      id?: string;
      firstNameAr?: string;
      lastNameAr?: string;
      firstNameEn?: string;
      lastNameEn?: string;
      phones?: Array<{ value?: string; number?: string; isPrimary?: boolean }>;
    } | null;
    relation?: string;
    isPrimaryEmergency?: boolean;
  }>;
}

@Component({
  selector: 'app-students',
  templateUrl: './students.component.html',
  styleUrls: ['./students.component.css']
})
export class StudentsComponent implements OnInit {
  items: StudentListItem[] = [];
  filteredItems: StudentListItem[] = [];
  selected: StudentListItem[] = [];
  classes: any[] = [];
  selectedClassId = '';
  searchTerm = '';
  pageSize = 10;
  pageIndex = 0;
  pageSizeOptions = [10, 20, 50];
  showDeleteModal = false;
  deleteModalBody = '';
  deleteModalParams: Record<string, unknown> = {};
  isError = false;
  errorMessage = '';
  exportingCards = false;
  exportingList = false;
  private schoolName = '';
  private cardColors: StudentCardColors = { ...DEFAULT_STUDENT_CARD_COLORS };

  constructor(
    private studentsService: StudentsService,
    private classesService: ClassesService,
    private studentCardsPdfService: StudentCardsPdfService,
    private studentsListPdfService: StudentsListPdfService,
    private locationsService: LocationsService,
    private schoolSettingsService: SchoolSettingsService,
    private translate: TranslateService,
    private router: Router,
    private languageService: LanguageService,
  ) {}

  get exporting(): boolean {
    return this.exportingCards || this.exportingList;
  }

  async ngOnInit() {
    const [_, settings, classes] = await Promise.all([
      this.fetchItems(),
      this.schoolSettingsService.get().catch(() => null),
      this.classesService.list().catch(() => []),
    ]);
    this.schoolName = settings?.schoolName || '';
    this.cardColors = {
      busBackground: settings?.studentCardColors?.busBackground || DEFAULT_STUDENT_CARD_COLORS.busBackground,
      noBusBackground: settings?.studentCardColors?.noBusBackground || DEFAULT_STUDENT_CARD_COLORS.noBusBackground,
      cutMarginMm: settings?.studentCardColors?.cutMarginMm ?? DEFAULT_STUDENT_CARD_COLORS.cutMarginMm,
    };
    this.classes = (classes || [])
      .slice()
      .sort((a: any, b: any) => String(a.name || '').localeCompare(String(b.name || ''), 'ar'));
  }

  get searchPlaceholder(): string {
    return this.translate.instant('COMMON.SEARCH');
  }

  get allClassesLabel(): string {
    return this.translate.instant('STUDENTS.ALL_CLASSES');
  }

  get classOptions(): SearchableSelectOption[] {
    return this.classes.map(cls => ({
      value: String(cls.id || cls._id || ''),
      label: cls.major?.name ? `${cls.name} — ${cls.major.name}` : (cls.name || ''),
      searchText: `${cls.name || ''} ${cls.major?.name || ''}`,
    })).filter(option => option.value);
  }

  async fetchItems() {
    this.items = await this.studentsService.list();
    this.applyFilter();
  }

  displayName(item: StudentListItem): string {
    const isAr = this.languageService.current === 'ar';
    return isAr
      ? `${item.firstNameAr || ''} ${item.lastNameAr || ''}`.trim()
      : `${item.firstNameEn || ''} ${item.lastNameEn || ''}`.trim();
  }

  statusKey(status?: string): string {
    return `STUDENTS.STATUS_${(status || 'active').toUpperCase()}`;
  }

  get paginatedItems(): StudentListItem[] {
    const start = this.pageIndex * this.pageSize;
    return this.filteredItems.slice(start, start + this.pageSize);
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredItems.length / this.pageSize));
  }

  get pageStart(): number {
    return this.filteredItems.length ? this.pageIndex * this.pageSize + 1 : 0;
  }

  get pageEnd(): number {
    return Math.min((this.pageIndex + 1) * this.pageSize, this.filteredItems.length);
  }

  applyFilter() {
    const term = this.searchTerm.trim().toLowerCase();
    this.filteredItems = this.items.filter(item => {
      if (this.selectedClassId && String(item.class?.id || '') !== this.selectedClassId) {
        return false;
      }
      if (!term) {
        return true;
      }
      return (
        this.displayName(item).toLowerCase().includes(term) ||
        !!item.studentCode?.toLowerCase().includes(term) ||
        !!item.class?.name?.toLowerCase().includes(term) ||
        !!item.status?.toLowerCase().includes(term)
      );
    });
    this.pageIndex = 0;
  }

  onSearchChange() {
    this.applyFilter();
  }

  onClassChange() {
    this.clearSelection();
    this.applyFilter();
  }

  onPageSizeChange(size: string) {
    this.pageSize = Number(size);
    this.pageIndex = 0;
  }

  previousPage() {
    if (this.pageIndex > 0) {
      this.pageIndex--;
    }
  }

  nextPage() {
    if (this.pageIndex < this.totalPages - 1) {
      this.pageIndex++;
    }
  }

  isSelected(item: StudentListItem): boolean {
    return this.selected.some(selected => selected.id === item.id);
  }

  toggleSelection(item: StudentListItem, event: Event) {
    event.stopPropagation();
    this.selected = this.isSelected(item)
      ? this.selected.filter(selected => selected.id !== item.id)
      : [...this.selected, item];
  }

  clearSelection() {
    this.selected = [];
  }

  onAdd() {
    this.router.navigate(portalCommands(this.router, 'settings', 'students', 'new'));
  }

  openDetails(item: StudentListItem) {
    this.router.navigate(portalCommands(this.router, 'settings', 'students', item.id));
  }

  onEdit() {
    if (this.selected.length === 1) {
      this.router.navigate(portalCommands(this.router, 'settings', 'students', this.selected[0].id, 'edit'));
    }
  }

  onDeleteMany() {
    this.deleteModalBody = this.selected.length === 1 ? 'STUDENTS.DELETE_ONE' : 'STUDENTS.DELETE_MANY';
    this.deleteModalParams = this.selected.length === 1
      ? { name: this.displayName(this.selected[0]) }
      : { count: this.selected.length };
    this.showDeleteModal = true;
  }

  async onDeleteConfirm(confirmed: boolean) {
    if (confirmed) {
      for (const item of this.selected) {
        try {
          await this.studentsService.delete(item.id);
        } catch (error: any) {
          this.isError = true;
          this.errorMessage = error?.error?.errorMessages?.[0]?.message || 'STUDENTS.DELETE_FAILED';
          this.showDeleteModal = false;
          await this.fetchItems();
          return;
        }
      }
      this.selected = [];
      await this.fetchItems();
    }
    this.showDeleteModal = false;
  }

  async printCardsPdf(): Promise<void> {
    if (this.exporting) {
      return;
    }
    if (!this.selectedClassId) {
      this.isError = true;
      this.errorMessage = 'STUDENTS.PDF_SELECT_CLASS';
      return;
    }

    const source = this.selected.length ? this.selected : this.filteredItems;
    if (!source.length) {
      this.isError = true;
      this.errorMessage = 'STUDENTS.PDF_EMPTY';
      return;
    }

    this.exportingCards = true;
    this.isError = false;
    this.errorMessage = '';
    try {
      await this.studentCardsPdfService.export({
        schoolName: this.schoolName,
        isArabic: this.languageService.current === 'ar',
        cardColors: this.cardColors,
        labels: {
          title: this.translate.instant('STUDENTS.PDF_CARD_TITLE'),
          classLabel: this.translate.instant('STUDENTS.CLASS'),
          noClass: this.translate.instant('STUDENTS.PDF_NO_CLASS'),
          noQr: this.translate.instant('STUDENTS.PDF_NO_QR'),
        },
        students: source.map(item => ({
          id: item.id,
          nameAr: `${item.firstNameAr || ''} ${item.lastNameAr || ''}`.trim(),
          nameEn: `${item.firstNameEn || ''} ${item.lastNameEn || ''}`.trim(),
          className: item.class?.name || '',
          busText: item.bus?.number ? `${item.bus.number} - BUS` : '',
          qrCode: String(item.qrCode || '').trim(),
        })),
      });
    } catch {
      this.isError = true;
      this.errorMessage = 'STUDENTS.PDF_FAILED';
    } finally {
      this.exportingCards = false;
    }
  }

  async printListPdf(): Promise<void> {
    if (this.exporting) {
      return;
    }
    if (!this.selectedClassId) {
      this.isError = true;
      this.errorMessage = 'STUDENTS.PDF_SELECT_CLASS';
      return;
    }

    const source = this.selected.length ? this.selected : this.filteredItems;
    if (!source.length) {
      this.isError = true;
      this.errorMessage = 'STUDENTS.LIST_PDF_EMPTY';
      return;
    }

    this.exportingList = true;
    this.isError = false;
    this.errorMessage = '';
    try {
      const isArabic = this.languageService.current === 'ar';
      const groups = new Map<string, { classId: string; className: string; students: StudentListItem[] }>();

      for (const item of source) {
        const classId = String(item.class?.id || '') || '__none__';
        const className = item.class?.name
          || this.translate.instant('STUDENTS.PDF_NO_CLASS');
        if (!groups.has(classId)) {
          groups.set(classId, { classId, className, students: [] });
        }
        groups.get(classId)!.students.push(item);
      }

      const ordered = Array.from(groups.values()).sort((a, b) =>
        a.className.localeCompare(b.className, isArabic ? 'ar' : 'en'),
      );

      const locationNames = await this.resolveLocationNames(source, isArabic);

      await this.studentsListPdfService.export({
        schoolName: this.schoolName,
        isArabic,
        labels: {
          title: this.translate.instant('STUDENTS.LIST_PDF_TITLE'),
          classLabel: this.translate.instant('STUDENTS.CLASS'),
          total: this.translate.instant('STUDENTS.LIST_PDF_TOTAL'),
          noClass: this.translate.instant('STUDENTS.PDF_NO_CLASS'),
          empty: this.translate.instant('STUDENTS.LIST_PDF_EMPTY'),
          index: '#',
          nameAr: this.translate.instant('STUDENTS.LIST_PDF_NAME_AR'),
          className: this.translate.instant('STUDENTS.CLASS'),
          major: this.translate.instant('STUDENTS.LIST_PDF_MAJOR'),
          bus: this.translate.instant('STUDENTS.BUS'),
          father: this.translate.instant('STUDENTS.LIST_PDF_FATHER'),
          mother: this.translate.instant('STUDENTS.LIST_PDF_MOTHER'),
          guardian: this.translate.instant('STUDENTS.LIST_PDF_GUARDIAN'),
          emergency: this.translate.instant('STUDENTS.PRIMARY_EMERGENCY'),
          area: this.translate.instant('STUDENTS.AREA'),
          notes: this.translate.instant('STUDENTS.LIST_PDF_NOTES'),
        },
        classes: ordered.map(group => ({
          classId: group.classId,
          className: group.className,
          rows: group.students
            .slice()
            .sort((a, b) => this.displayName(a).localeCompare(this.displayName(b), isArabic ? 'ar' : 'en'))
            .map(item => this.toListPdfRow(item, isArabic, locationNames)),
        })),
      });
    } catch {
      this.isError = true;
      this.errorMessage = 'STUDENTS.LIST_PDF_FAILED';
    } finally {
      this.exportingList = false;
    }
  }

  private async resolveLocationNames(
    students: StudentListItem[],
    isArabic: boolean,
  ): Promise<{ emirates: Map<string, string>; areas: Map<string, string> }> {
    const emirates = new Map<string, string>();
    const areas = new Map<string, string>();

    try {
      const list = await this.locationsService.listEmirates();
      for (const emirate of list || []) {
        const code = String(emirate?.code || '').trim();
        if (!code) {
          continue;
        }
        emirates.set(
          code,
          isArabic
            ? (emirate.nameAr || emirate.nameEn || code)
            : (emirate.nameEn || emirate.nameAr || code),
        );
      }
    } catch {
      // Keep raw emirate codes if lookup fails.
    }

    const emirateCodes = Array.from(new Set(
      students
        .map(item => String(item.address?.emirate || '').trim())
        .filter(Boolean),
    ));

    await Promise.all(emirateCodes.map(async emirate => {
      try {
        const list = await this.locationsService.listAreas(emirate);
        for (const area of list || []) {
          const code = String(area?.code || '').trim();
          if (!code) {
            continue;
          }
          const label = isArabic
            ? (area.nameAr || area.nameEn || code)
            : (area.nameEn || area.nameAr || code);
          areas.set(`${emirate}::${code}`, label);
          if (!areas.has(code)) {
            areas.set(code, label);
          }
        }
      } catch {
        // Keep raw area codes if lookup fails.
      }
    }));

    return { emirates, areas };
  }

  private toListPdfRow(
    item: StudentListItem,
    isArabic: boolean,
    locationNames: { emirates: Map<string, string>; areas: Map<string, string> },
  ) {
    const emirateCode = String(item.address?.emirate || '').trim();
    const areaCode = String(item.address?.area || '').trim();
    const areaName = areaCode
      ? (locationNames.areas.get(`${emirateCode}::${areaCode}`)
        || locationNames.areas.get(areaCode)
        || areaCode)
      : '';
    const line1 = String(item.address?.line1 || '').trim();

    return {
      nameAr: `${item.firstNameAr || ''} ${item.lastNameAr || ''}`.trim(),
      className: item.class?.name || '',
      major: item.class?.major?.name || '',
      bus: item.bus?.number || item.bus?.name || '',
      father: this.parentContactByRelation(item, 'father', isArabic),
      mother: this.parentContactByRelation(item, 'mother', isArabic),
      guardian: this.parentContactByRelation(item, 'guardian', isArabic),
      emergency: this.emergencyContact(item, isArabic),
      area: areaName || line1,
      notes: item.address?.landmark || '',
    };
  }

  private parentContactByRelation(
    item: StudentListItem,
    relation: string,
    isArabic: boolean,
  ): string {
    const link = (item.parents || []).find(
      entry => String(entry.relation || '').trim().toLowerCase() === relation,
    );
    if (!link?.parent) {
      return '';
    }
    const name = isArabic
      ? `${link.parent.firstNameAr || ''} ${link.parent.lastNameAr || ''}`.trim()
      : `${link.parent.firstNameEn || ''} ${link.parent.lastNameEn || ''}`.trim();
    const phones = link.parent.phones || [];
    const phone = this.primaryContactValue(phones);
    return [name, phone].filter(Boolean).join(' · ');
  }

  private emergencyContact(item: StudentListItem, isArabic: boolean): string {
    const link = (item.parents || []).find(entry => entry.isPrimaryEmergency)
      || (item.parents || [])[0];
    if (!link?.parent) {
      return '';
    }
    const relation = this.parentRelationLabel(link.relation);
    const name = isArabic
      ? `${link.parent.firstNameAr || ''} ${link.parent.lastNameAr || ''}`.trim()
      : `${link.parent.firstNameEn || ''} ${link.parent.lastNameEn || ''}`.trim();
    const phone = this.primaryContactValue(link.parent.phones);
    return [relation, name, phone].filter(Boolean).join(' · ');
  }

  private parentRelationLabel(relation?: string): string {
    switch (String(relation || '').trim().toLowerCase()) {
      case 'father':
        return this.translate.instant('STUDENTS.LIST_PDF_FATHER');
      case 'mother':
        return this.translate.instant('STUDENTS.LIST_PDF_MOTHER');
      case 'guardian':
        return this.translate.instant('STUDENTS.LIST_PDF_GUARDIAN');
      default:
        return '';
    }
  }

  private primaryContactValue(
    entries?: Array<{ value?: string; number?: string; address?: string; isPrimary?: boolean }>,
  ): string {
    const list = entries || [];
    const primary = list.find(entry => entry.isPrimary) || list[0];
    return this.contactValue(primary);
  }

  private contactValue(
    entry?: { value?: string; number?: string; address?: string } | null,
  ): string {
    if (!entry) {
      return '';
    }
    return String(entry.value || entry.number || entry.address || '').trim();
  }
}
